import { useEffect, useRef, useState } from 'react';
import { useMedia } from 'react-use';
import { twMerge } from 'tailwind-merge';

import Button from 'molecules/Button';
import Dropdown from 'molecules/Dropdown';
import Icon from 'molecules/Icon';
import TextField from 'molecules/TextField';

import ReactPortal from 'components/Modal/reactPortal';

import screens from 'theme/spacing/screens';

import type { ContentfulTaxonomyTag } from 'graphqlTypes';
import type { FC } from 'react';

type FilterValuesState = {
  industry?: string;
  tags: string[];
};

export type FilterProps = {
  activeSort?: string;
  authorSlug?: string;
  tags: ContentfulTaxonomyTag[];
  resourceType: string;
  filterValues?: { industry?: string; tags?: string[] };
  setFilterValues: (newFilters: FilterValuesState | ((prevFilters: FilterValuesState) => FilterValuesState)) => void;
  searchValue?: string;
  setSortValue?: (value: string) => void;
  setSearchValue?: (value: string) => void;
  setToBasePage?: () => void;
};

const sortDropdownValues = [{ label: 'Most Recent' }, { label: 'Oldest' }];

const Filters: FC<FilterProps> = ({
  activeSort,
  authorSlug,
  tags,
  filterValues,
  resourceType,
  setFilterValues,
  searchValue,
  setSearchValue,
  setSortValue,
  setToBasePage,
}) => {
  const isMobile = useMedia(`(max-width: ${screens.sm})`, false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const topTags = tags.slice(0, 3);
  const selectedTags = filterValues?.tags;
  const dropdownDefaultValue = activeSort === 'Most Recent' ? 0 : 1;
  const areTagsAdded = tags?.some(({ name }) => filterValues?.tags?.includes(name));

  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isSearchOpen) {
      searchInputRef.current?.focus();
    }
  }, [isSearchOpen]);

  const handleTagClick = (tag: ContentfulTaxonomyTag) => {
    setFilterValues(prevValues => {
      const updatedTags = prevValues.tags.includes(tag)
        ? prevValues.tags.filter(t => t !== tag)
        : [...prevValues.tags, tag];

      if (typeof window !== 'undefined') {
        window.history.pushState({}, '', authorSlug ? `/${resourceType}/${authorSlug}` : `/${resourceType}`);
      }

      return {
        ...prevValues,
        tags: updatedTags,
      };
    });
  };

  const handleChange = e => {
    setToBasePage?.();

    const value = e.target.value;
    setSearchValue(value);
  };

  const handleClearTags = () => {
    setFilterValues(prevValues => ({
      ...prevValues,
      tags: prevValues.tags.filter(tag => topTags.includes(tag)),
    }));
  };

  const handleSort = value => {
    setSortValue(value);
  };

  const BackArrow = ({ className }: { className?: string }) => (
    <div
      className={twMerge('back-arrow z-10 flex h-[60px] w-full items-center bg-white', className)}
      data-cy="backArrow"
    >
      <button className="ml-5 flex w-full items-center hover:cursor-pointer" onClick={() => setIsModalOpen(false)}>
        <Icon icon="arrow-back-secondary" size={16} className="mr-2 fill-primary-600" role="presentation" />
        <p className="font-medium text-primary-600" role="presentation">
          Back
        </p>
      </button>
    </div>
  );

  return (
    <div
      className="flex w-full flex-col-reverse justify-between sm:h-[32px] sm:flex-row sm:items-center"
      data-cy="quickSearchRow"
    >
      <div className="filter-btns flex">
        <div className="search flex h-[28px] max-w-full items-center" data-cy="quickSearch">
          <div
            className={`search-btn mr-2 flex h-[28px] w-[32px] items-center justify-center bg-gray-100 hover:cursor-pointer ${
              isSearchOpen || searchValue ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-900'
            }`}
            onClick={() => setIsSearchOpen(!isSearchOpen)}
            role="presentation"
          >
            <Icon icon="search" size={20} className={isSearchOpen || searchValue ? 'fill-white' : 'fill-gray-900'} />
          </div>
          {isSearchOpen && (
            <>
              <TextField
                name="search"
                className="m-0 w-[372px] [&>*]:m-0"
                inputClassName="h-[28px] border-b-1 border-t-0 border-l-0 border-r-0 border-gray-900 pl-0 shadow-transparent placeholder-gray-900"
                placeholder="Search..."
                onChange={e => handleChange(e)}
                value={searchValue}
                inputRef={searchInputRef}
              />
              <Icon
                icon="x"
                size={12}
                className="z-10 -ml-[12px] hover:cursor-pointer"
                onClick={() => (searchValue.length === 0 ? setIsSearchOpen(false) : setSearchValue(''))}
              />
            </>
          )}
        </div>
        {!isSearchOpen && (
          <>
            {tags.length > 0 && (
              <div
                className={`filter-btn mr-2 flex h-[28px] w-[32px] items-center justify-center ${
                  areTagsAdded ? 'bg-primary-600' : 'bg-gray-100'
                }`}
              >
                <Icon
                  data-cy="openFilterModal"
                  icon="filter"
                  size={20}
                  onClick={() => setIsModalOpen(true)}
                  className={`hover:cursor-pointer ${areTagsAdded ? 'text-white' : 'text-gray-900'}`}
                />
                {isModalOpen && (
                  <ReactPortal
                    dataCyAttribute="filterModal"
                    wrapperId="modal"
                    wrapperClass="resource-filter-modal flex items-start pt-20 pb-32 md:py-24 !overflow-auto md:pt-0 justify-center h-screen w-full bg-white fixed top-0 left-0 z-[999]"
                  >
                    <BackArrow className="fixed left-0 top-0 flex sm:hidden" />
                    <div className="relative m-auto flex max-w-[calc(100vw-40px)] flex-col items-center justify-center gap-5 lg:max-w-[60vw]">
                      <BackArrow className="hidden sm:flex" />
                      <h2 className="text-center text-2xl font-semibold">Select the filters relevant to your search</h2>
                      <div className="flex flex-wrap items-center justify-start gap-3 md:justify-center">
                        {tags?.map(({ name }, index) => (
                          <div
                            data-cy={`filter-${name?.toLowerCase().split(' ').join('-')}`}
                            className={`filter-btn filter-modal-btn mr-2 flex items-center justify-center px-[10px] py-[8px] hover:cursor-pointer ${
                              selectedTags.includes(name) ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-900'
                            }`}
                            key={`top-tag-${index}`}
                            onClick={() => handleTagClick(name)}
                            role="presentation"
                          >
                            <p className="text-sm">{name}</p>
                          </div>
                        ))}
                      </div>

                      <div className="fixed bottom-0 left-0 flex w-full flex-col items-center justify-center bg-white p-5 sm:absolute sm:-bottom-32 sm:pb-6 sm:pt-0 md:left-auto">
                        <div className="flex w-full gap-5 sm:w-auto">
                          <Button
                            data-cy="filter-clear"
                            styles="w-[50%] sm:w-max flex items-center hover:cursor-pointer !bg-white hover:!bg-gray-100 px-6 py-4 border border-black [&>div]:!bg-yellow-400 focus:!border-black"
                            onClick={() => handleClearTags()}
                            role="presentation"
                            hierarchy="secondary"
                          >
                            Clear
                          </Button>
                          <Button
                            data-cy="filter-see-results"
                            onClick={() => areTagsAdded && setIsModalOpen(false)}
                            styles={twMerge(
                              '!w-[50%] sm:!w-max w-full px-6 py-4',
                              areTagsAdded
                                ? '!bg-primary-600 text-white !cursor-pointer'
                                : '!bg-gray-300 !text-gray-500 !cursor-auto [&>div]:!bg-transparent',
                            )}
                            role="presentation"
                          >
                            See Results
                          </Button>
                        </div>
                      </div>
                    </div>
                  </ReactPortal>
                )}
              </div>
            )}
            <div
              data-cy="quickSearchOption-1"
              onClick={() => handleClearTags()}
              className={`filter-btn filter-all-btn mr-2 flex h-[28px] items-center justify-center px-[10px] hover:cursor-pointer ${
                selectedTags.length === 0 ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-900'
              }`}
              role="presentation"
            >
              <p className="text-sm">All</p>
            </div>
            {!isMobile &&
              topTags?.map(({ name }, index) => (
                <div
                  data-cy={`quickSearchOption-${index + 2}`}
                  className={`filter-btn filter-top-btn mr-2 flex h-[28px] items-center justify-center px-[10px] hover:cursor-pointer ${
                    selectedTags.includes(name) ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-900'
                  }`}
                  key={`top-tag-${index}`}
                  onClick={() => handleTagClick(name)}
                  role="presentation"
                >
                  <p className="text-sm">{name}</p>
                </div>
              ))}
            <div
              className={`flex size-[28px] items-center justify-center rounded-full bg-gray-100 hover:cursor-pointer ${
                selectedTags?.length === 0 && searchValue?.length === 0 ? 'hidden' : 'block'
              }`}
              data-cy="quickSearchClear"
              onClick={() => {
                setFilterValues(() => ({
                  industry: null,
                  tags: [],
                }));
                setSearchValue('');
              }}
              role="presentation"
            >
              <Icon icon="x" size={20} />
            </div>
          </>
        )}
      </div>
      <Dropdown
        className="dropdown-parent relative mb-7 flex w-full items-center justify-between border border-solid border-gray-300 bg-white px-[14px] py-[10px] text-left text-md font-normal text-gray-500 shadow-xs sm:my-0 sm:w-[166px] sm:py-[3px]"
        popupClassName="top-[50px] sm:top-[35px]"
        label="Most Recent"
        items={sortDropdownValues}
        type="filter"
        defaultValue={dropdownDefaultValue}
        onChange={e => {
          const selectedValue = sortDropdownValues[e]?.label || null;

          handleSort(selectedValue);
        }}
      />
    </div>
  );
};

export default Filters;
